import { connect, useDispatch, useSelector } from 'react-redux';
import defaultApi from 'api';
import { mapObjectsByAttr } from 'utils/functions';
import { updateParams } from 'utils/urls';
import { fromPairs, map, omit } from 'lodash';
import { defaultMergeProps } from 'utils/reduxHelpers';
import { errorResponseFormatter } from 'utils/apiHelpers';

import Manager from './manager';

export const defaultDefaultState = {
    items: {}, // <id>: <item>
    itemIds: [], // [<id>]
    options: {}, // saved from OPTIONS request
    meta: {
        count: 0, // Total number of records on server (not local)
        self: '', // Current API url
        next: '', // Next API url
        prev: '', // Previous API url
    },
    listPath: '',
};

const reduxify = ({ name, idAttr = 'id', extraReducers = {}, extraDefaultState }) => {
    const defaultState = {
        ...defaultDefaultState,
        ...extraDefaultState,
    };

    const SLUG = name.toUpperCase();

    // {SET_FOO_STUFF: () => ...} -> {SET_STUFF: 'SET_FOO_STUFF'}
    const extraConstants = fromPairs(
        map(extraReducers, (func, constant) => [
            constant.replace(`${SLUG}_`, ''),
            constant,
        ])
    );

    const constants = {
        SET_OPTIONS: `SET_${SLUG}_OPTIONS`,
        UPDATE_META: `SET_${SLUG}_META`,
        REPLACE_ITEMS: `REPLACE_${SLUG}_ITEMS`,
        APPEND_ITEMS: `APPEND_${SLUG}_ITEMS`,
        CLEAR_IDS: `CLEAR_${SLUG}_IDS`,
        SET_ITEM: `SET_${SLUG}_ITEM`,
        SET_LIST_PATH: `SET_${SLUG}_LIST_PATH`,
        DELETE_ITEM: `DELETE_${SLUG}_ITEM`,
        ...extraConstants,
    };

    const reducerFuncs = {
        // Save results of OPTION requests for getting valid filters, etc from server
        [constants.SET_OPTIONS]: (state = defaultState, action = {}) => {
            return { ...state, options: action.options };
        },

        // list meta actions
        [constants.UPDATE_META]: (state = defaultState, action = {}) => {
            return {
                ...state,
                meta: { ...state.meta, ...action.meta },
            };
        },

        // item list, replace entire store
        [constants.REPLACE_ITEMS]: (state = defaultState, action = {}) => {
            return {
                ...state,
                items: mapObjectsByAttr(action.items, idAttr),
                itemIds: action.items.map(obj => obj[idAttr]),
            };
        },

        // item list, add to existing items
        [constants.APPEND_ITEMS]: (state = defaultState, action = {}) => {
            return {
                ...state,
                items: {
                    ...state.items,
                    ...mapObjectsByAttr(action.items, idAttr),
                },
                itemIds: [...state.itemIds, ...action.items.map(obj => obj[idAttr])],
            };
        },

        // simply clears itemIds, but not items
        // useful to quickly/temporarily clear list without forcing reset of all data
        [constants.CLEAR_IDS]: (state = defaultState, action = {}) => {
            return { ...state, itemIds: [] };
        },

        // Update a single record
        [constants.SET_ITEM]: (state = defaultState, action = {}) => {
            return {
                ...state,
                items: {
                    ...state.items,
                    [action.item[idAttr]]: action.item,
                },
            };
        },

        // Delete a single record
        [constants.DELETE_ITEM]: (state = defaultState, action = {}) => {
            return {
                ...state,
                items: omit(state.items, action.id),
            };
        },

        // update list view state
        [constants.SET_LIST_PATH]: (state = defaultState, action = {}) => {
            return {
                ...state,
                listPath: action.path,
            };
        },

        ...extraReducers,
    };

    const reducer = (state = defaultState, action = {}) => {
        const handler = reducerFuncs[action.type];
        if (handler) {
            return handler(state, action);
        } else {
            return state;
        }
    };

    return {
        constants,
        reducer,
        defaultState,
    };
};

class Collection {
    constructor({
        name,
        api = defaultApi,
        idAttr = 'id',
        extraReducers = {},
        extraDefaultState = {},
        listUrl = '',
    }) {
        // extraActions is a mapping of constant to reducer function:
        // i.e.
        // {
        //     FROBNICATE: (action, state) => ({...state, frobnicating: true})
        // }
        //
        // action.type === 'FROBNICATE' will be passed to this function
        const { constants, reducer, defaultState } = reduxify({
            name,
            idAttr,
            extraReducers,
            extraDefaultState,
        });
        this.constants = constants;
        this.reducer = reducer;
        this.defaultState = defaultState;
        this.api = api;
        this.idAttr = idAttr;
        this.name = name;
        this.listUrl = listUrl;

        this.fetchOptions = this.fetchOptions.bind(this);
        this._fetchItems = this._fetchItems.bind(this);
        this.fetchItems = this.fetchItems.bind(this);
        this.replaceItems = this.replaceItems.bind(this);
        this.appendItems = this.appendItems.bind(this);
        this.clearItems = this.clearItems.bind(this);
        this.fetchItem = this.fetchItem.bind(this);

        // TODO
        //this.deleteItem = this.deleteItem.bind(this);
        this.postItem = this.postItem.bind(this);
        //this.patchItem = this.patchItem.bind(this);

        this.connect = this.connect.bind(this);
        this.mapStateToProps = this.mapStateToProps.bind(this);
        this.mapDispatchToProps = this.mapDispatchToProps.bind(this);
        this.mapDispatchToActions = this.mapDispatchToActions.bind(this);
        this.buildUrl = this.buildUrl.bind(this);
    }

    buildUrl(url, params, fail) {
        if (!url) {
            const error = new Error(`Missing url for collection '${this.name}'`);
            if (fail) {
                fail(error);
            } else {
                throw error;
            }
        }

        return updateParams(this.api.apiUrl(url), params);
    }

    setListPath(path) {
        const { SET_LIST_PATH } = this.constants;
        return dispatch => dispatch({ type: SET_LIST_PATH, path });
    }

    fetchOptions({ url, params, start, done, fail, signal }) {
        const { SET_OPTIONS } = this.constants;
        url = this.buildUrl(url, params, fail);

        return dispatch => {
            start && start();
            this.api
                .options(url, { signal: signal })
                .then(options => {
                    dispatch({ type: SET_OPTIONS, options });
                    done && done(options);
                })
                .catch(error => {
                    fail && fail(error.message, { error });
                });
        };
    }

    _fetchItems({ url, params, start, done, fail, signal, append = false }) {
        const { APPEND_ITEMS, REPLACE_ITEMS, UPDATE_META } = this.constants;
        const action = append ? APPEND_ITEMS : REPLACE_ITEMS;
        url = this.buildUrl(url, params, fail);

        return dispatch => {
            start && start();
            this.api
                .get(url, { signal })
                .then(response => {
                    const meta = { ...response.meta, ...response.links }; // merge these together for our purposes
                    dispatch({ type: UPDATE_META, meta });
                    dispatch({
                        type: action,
                        items: response.data,
                    });
                    done && done(response);
                })
                .catch(error => {
                    fail && fail(error.message, { error });
                });
        };
    }

    fetchItems({ url, params, start, done, fail, signal }) {
        return this._fetchItems({
            url,
            params,
            start,
            done,
            fail,
            signal,
            append: false,
        });
    }

    replaceItems({ url, params, start, done, fail, signal }) {
        // alias to fetchItems
        return this._fetchItems({
            url,
            params,
            start,
            done,
            fail,
            signal,
            append: false,
        });
    }

    appendItems({ url, params, start, done, fail, signal }) {
        return this._fetchItems({ url, start, done, fail, signal, append: true });
    }

    clearItems({ start, done }) {
        const { CLEAR_IDS } = this.constants;
        return dispatch => {
            start && start();
            dispatch({ type: CLEAR_IDS });
            done && done();
        };
    }

    fetchItem({ url, params, start, done, fail, signal }) {
        const { SET_ITEM } = this.constants;
        url = this.buildUrl(url, params, fail);

        return dispatch => {
            start && start();
            this.api
                .get(url, { signal })
                .then(item => {
                    dispatch({ type: SET_ITEM, item });
                    done && done(item);
                })
                .catch(error => fail && fail(error.message, { error }));
        };
    }

    // TODO
    deleteItem({ url, id, start, done, fail, signal }) {
        const { DELETE_ITEM } = this.constants;
        url = this.buildUrl(url);

        return dispatch => {
            start && start();
            this.api
                .delete(url, { signal })
                .then(response => {
                    if (id) {
                        dispatch({ type: DELETE_ITEM, id });
                    }
                    done && done(response);
                })
                .catch(error => {
                    fail && fail(error.message);
                });
        };
    }

    _updateItem(method = 'post', { url, params, data, start, done, fail, signal }) {
        const { SET_ITEM } = this.constants;
        url = this.buildUrl(url, params, fail);

        // method should be 'post' or 'patch' or 'put'
        const handler = this.api[method];

        return dispatch => {
            start && start();
            handler(url, data, { signal })
                .then(item => {
                    dispatch({ type: SET_ITEM, item });
                    done && done(item);
                })
                .catch(error => {
                    const fieldErrors = errorResponseFormatter(error.json);
                    fail && fail(error.message, { fieldErrors, error });
                });
        };
    }

    patchItem({ url, params, data, start, done, fail, signal }) {
        return this._updateItem('patch', {
            url,
            params,
            data,
            start,
            done,
            fail,
            signal,
        });
    }

    putItem({ url, params, data, start, done, fail, signal }) {
        return this._updateItem('put', {
            url,
            params,
            data,
            start,
            done,
            fail,
            signal,
        });
    }

    postItem({ url, params, data, start, done, fail, signal }) {
        return this._updateItem('post', {
            url,
            params,
            data,
            start,
            done,
            fail,
            signal,
        });
    }

    mapDispatchToActions(dispatch, { listUrl = '', detailUrl = '' }) {
        // Note that any subclasses that add actions *will* need to extend this method:
        //
        //     mapDispatchToActions(dispatch, args={}) {
        //         const actions = super.mapDispatchToActions(dispatch, args);
        //
        //         // extra additional connected actions
        //         actions['doFoo'] = (args) => dispatch(foo(args));
        //
        //         return actions;
        //     }
        //
        //     TODO it'd be nice if there a way to do this automatically
        return {
            fetchOptions: (args = {}) =>
                dispatch(this.fetchOptions({ url: listUrl, ...args })),
            fetchItems: (args = {}) =>
                dispatch(this.fetchItems({ url: listUrl, ...args })),
            replaceItems: (args = {}) =>
                dispatch(this.replaceItems({ url: listUrl, ...args })),
            appendItems: (args = {}) =>
                dispatch(this.appendItems({ url: listUrl, ...args })),
            clearItems: (args = {}) => dispatch(this.clearItems(args)),
            fetchItem: (args = {}) =>
                dispatch(this.fetchItem({ url: detailUrl, ...args })),
            deleteItem: (args = {}) =>
                dispatch(this.deleteItem({ url: detailUrl, ...args })),
            patchItem: (args = {}) =>
                dispatch(this.patchItem({ url: detailUrl, ...args })),
            putItem: (args = {}) => dispatch(this.putItem({ url: detailUrl, ...args })),
            postItem: (args = {}) => dispatch(this.postItem({ url: listUrl, ...args })),
            setListPath: path => dispatch(this.setListPath(path)),
        };
    }

    connect(mapStateToProps, mapDispatchToProps, mergeProps, options = {}) {
        // wraps react-redux's connect by injecting:
        // - `actions`: object with dispatch-connected collection actions
        // - `collection`: app state
        //
        // all arguments are optional, but if provided, will be merged
        // with default implementations
        return connect(
            // do our own mapStateToProps then optionally chain passed function
            (state, ownProps) => {
                let newProps = this.mapStateToProps(state, ownProps);
                let otherProps = {};
                if (mapStateToProps) {
                    otherProps = mapStateToProps(state, { ...ownProps, ...newProps });
                }
                return { ...newProps, ...otherProps };
            },

            // do our own mapDispatchToProps then optionally chain passed function
            (dispatch, ownProps) => {
                let newProps = this.mapDispatchToProps(dispatch, ownProps);
                let otherProps = {};
                if (mapDispatchToProps) {
                    otherProps = mapDispatchToProps(dispatch, {
                        ...ownProps,
                        ...newProps,
                    });
                }
                return { ...newProps, ...otherProps };
            },

            // we don't want to pass separate 'collection' and 'actions',
            // we want to both them together as 'manager',
            // but since one needs state and one needs actions
            // we have to merge them together here...
            //
            // this means you can pass your own mergeProps function
            // and get ownProps.manager
            (stateProps, dispatchProps, ownProps) => {
                const manager = new Manager({
                    name: this.name,
                    collection: stateProps._collection,
                    actions: dispatchProps._actions,
                    id: ownProps.id,
                    listUrl: ownProps.listUrl || this.listUrl,
                });

                stateProps = omit(stateProps, ['_collection']);
                dispatchProps = omit(dispatchProps, ['_actions']);
                ownProps = { manager, ...ownProps };

                if (mergeProps) {
                    return mergeProps(stateProps, dispatchProps, ownProps);
                } else {
                    return defaultMergeProps(stateProps, dispatchProps, ownProps);
                }
            },

            options
        );
    }

    mapStateToProps(state, ownProps) {
        return {
            _collection: state[this.name],
        };
    }

    mapDispatchToProps(dispatch, ownProps) {
        const listUrl = ownProps.listUrl || this.listUrl;
        const detailUrl = ownProps.detailUrl;

        return {
            _actions: this.mapDispatchToActions(dispatch, { listUrl, detailUrl }),
        };
    }
}


export const useManager = ({ collection, listUrl = '', detailUrl = '', id = null }) => {
    const dispatch = useDispatch();
    const collectionState = useSelector(state => state[collection.name]);
    const actions = collection.mapDispatchToActions(dispatch, {
        listUrl: listUrl || collection.listUrl,
        detailUrl
    });

    return new Manager({
        name: collection.name,
        listUrl: listUrl || collection.listUrl,
        collection: collectionState,
        actions,
        id,
    });
};

export default Collection;
